'use client'
import { Box, SimpleGrid } from '@mantine/core'
import { useTranslations } from 'next-intl'

import CoinMetaDetails from '@/app/[locale]/price/[slug]/_components/About/CoinMetaDetails'
import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { useCoinMarketPair, useMarketCdcPair } from '@/services/coin/market'
import { ResourceLink, SocialLinkType } from '@/types/resource'

import { constructUrlDomain } from './utils'

interface ResourcesProps {
  slug: string
}

export default function Resources({ slug }: ResourcesProps) {
  const { coin } = useCoinContext()

  const t = useTranslations('about.resources')
  const {
    website_urls,
    whitepaper_urls_v2,
    explorer_urls,
    sourcecode_urls,
    sustainability_disclosure_urls,
  } = coin

  const { data: coinMarketPair } = useCoinMarketPair(slug)
  const { data: marketCdcPair } = useMarketCdcPair(slug)

  // Handle null data from API calls and provide fallbacks
  const coinMarketPairData =
    coinMarketPair?.filter(item => {
      if (!marketCdcPair) {
        // If marketCdcPair is null, skip comparisons involving it
        return false
      }
      return (
        item.market_pair_name !== marketCdcPair?.market_pair_name ||
        item.exchange_slug !== marketCdcPair?.exchange_slug
      )
    }) || []

  const resourcesLinks: ResourceLink[] = [
    {
      name: SocialLinkType.Explorers,
      label: t('explorers'),
      urls: explorer_urls,
    },
    {
      name: SocialLinkType.Whitepaper,
      label: t('whitepaper'),
      urls: whitepaper_urls_v2.map(({ url }) => url),
    },
    {
      name: SocialLinkType.Website,
      label: t('website'),
      urls: website_urls,
    },
    {
      name: SocialLinkType.Sourcecode,
      label: t('sourcecode'),
      urls: sourcecode_urls,
    },
    {
      name: SocialLinkType.Markets,
      label: t('markets'),
      urls: ['https://www.binance.com/en/markets/'],
    },
    {
      name: SocialLinkType.Disclosure,
      label: t('disclosure'),
      urls: sustainability_disclosure_urls,
    },
  ].filter(item => {
    if (!marketCdcPair && item.name === SocialLinkType.Markets) return false
    if (!Array.isArray(item.urls) || item.urls.length < 1) return false
    const urls = item.urls.filter(url => url && url.trim() !== '')
    item.urls = urls

    return !!urls.length
  })

  constructUrlDomain(resourcesLinks)

  return (
    <SimpleGrid
      cols={{ base: 2, md: resourcesLinks.length >= 4 ? 4 : resourcesLinks.length }}
      spacing={16}
      mt={48}
    >
      {resourcesLinks.map((item, idx) => (
        <Box key={idx}>
          <CoinMetaDetails socialData={item} marketPair={coinMarketPairData} />
        </Box>
      ))}
    </SimpleGrid>
  )
}
